import { redirect } from '@tanstack/react-router'
import { API_BASE_URL } from '@/config/api'
import { clearTokens } from '@/lib/token-storage'
import type { LoginRedirectSearch } from '@/lib/login-redirect-search'
import {
  buildSameOriginHrefWithoutSignedOut,
  isSignedOutSearch,
  stripSignedOutFromSearch,
} from '@/lib/signed-out-query'

type LoginSearchWithSignedOut = LoginRedirectSearch & { signed_out?: '1' }

/** Indica logout vindo do app principal (`?signed_out=1`). */
export function isSignedOutInUrl(): boolean {
  if (typeof window === 'undefined') return false
  return isSignedOutSearch({}, window.location.search)
}

/** Invalida o refresh cookie HttpOnly neste origin (via proxy Vite em dev). */
export async function revokeRefreshCookieSession(): Promise<void> {
  try {
    const base = API_BASE_URL ? API_BASE_URL.replace(/\/$/, '') : ''
    const refreshResponse = await fetch(`${base}/api/v2/auth/refresh-token`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    })
    if (!refreshResponse.ok) {
      return
    }

    const refreshData = (await refreshResponse.json()) as {
      accessToken?: string
    }
    const accessToken = refreshData?.accessToken
    if (!accessToken) {
      return
    }

    await fetch(`${base}/api/v2/auth/logout`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: '{}',
    })
  } catch {
    // sem cookie ou offline — segue limpando só o storage local
  }
}

/**
 * Após logout no app principal (`?signed_out=1`), remove tokens neste host
 * e limpa a URL (evita loop: login ainda tinha sessão salva em `/auth/import-session`).
 */
export async function redirectIfLoginSignedOutQuery(
  to: '/' | '/cadastro',
  search: LoginSearchWithSignedOut,
): Promise<void> {
  const signedOut = isSignedOutSearch(
    search,
    typeof window !== 'undefined' ? window.location.search : undefined,
  )
  if (!signedOut) {
    return
  }
  await revokeRefreshCookieSession()
  clearTokens()
  throw redirect({
    href: buildSameOriginHrefWithoutSignedOut(
      to,
      stripSignedOutFromSearch(search),
    ),
    replace: true,
  })
}
